import { AutoAwesomeOutlined, HelpOutlineOutlined, KeyboardArrowUpRounded } from '@mui/icons-material'
import { BEIGE_300, BOX_SHADOW_LG, GRAY_1000, GRAY_300, GRAY_700, WHITE } from 'constants/styling/theme'
import { Box, Button, Menu, MenuItem, Stack, Typography } from '@mui/material'
import { FC, ReactNode, useRef, useState } from 'react'

import { MUITooltip } from 'components/common/MUITooltip'
import { useTranslation } from 'react-i18next'

/** One entry of the run-source menu behind the chevron: which image the next generation starts from. */
export interface GenerateSourceOption {
  id: string
  /** Thumbnail of the candidate image */
  imageUrl: string
  title: string
  description: string
  onSelect: () => void
}

interface GenerateButtonProps {
  /** Callback when button is clicked */
  onGenerate: () => void
  /** Whether the button is disabled */
  disabled?: boolean
  /** Tooltip text to show when disabled */
  disabledTooltip?: string
  /** Padding value for the wrapper Box (default: '2.3rem') */
  padding?: string
  /** Button text; defaults to "Generate" */
  label?: string
  /** Leading icon; defaults to the Generate sparkles (the Lab's Subscribe CTA uses a lock) */
  icon?: ReactNode
  /**
   * The "Need something more specific?" help: `inline` = help icon inside the CTA (the ai-visuals
   * panels), `none` = no help at all (Lab SDK panels — product decision 2026-09-04).
   */
  helpPlacement?: 'inline' | 'none'
  /**
   * Images the next generation can start from. When given, the CTA becomes a split button: the main part keeps
   * `onGenerate` (the default source) and a chevron opens the list above the button. Empty/undefined = plain CTA.
   */
  sourceOptions?: GenerateSourceOption[]
  /** A quiet line under the CTA (the "Display all features at once" view lists what the run includes) */
  caption?: ReactNode
}

const CTA_BUTTON_SX = {
  backgroundColor: GRAY_1000,
  color: WHITE,
  textTransform: 'none',
  boxShadow: '0px 1px 4px 0px rgba(19, 24, 29, 0.06)',
  '&:hover': {
    backgroundColor: GRAY_1000,
    opacity: 0.9,
  },
  '&:disabled': {
    backgroundColor: GRAY_1000,
    opacity: 0.5,
    color: WHITE,
    cursor: 'not-allowed',
    '& .MuiTypography-root': {
      color: WHITE,
    },
    '& .MuiSvgIcon-root': {
      color: WHITE,
    },
  },
} as const

/**
 * Shared Generate Button component for Photo Studio panels.
 * Used in Magic Eraser and Virtual Staging panels.
 * 
 * @example
 * <GenerateButton
 *   onGenerate={handleGenerate}
 *   disabled={isProcessing}
 *   disabledTooltip="Please wait for processing to complete"
 *   padding="2.3rem"
 * />
 */
export const GenerateButton: FC<GenerateButtonProps> = ({
  onGenerate,
  disabled = false,
  disabledTooltip,
  padding = '2.3rem',
  label,
  icon,
  helpPlacement = 'inline',
  sourceOptions,
  caption,
}) => {
  const { t } = useTranslation(['photo_studio_editing'])
  const hasSourceMenu = !!sourceOptions && sourceOptions.length > 0
  const [isSourceMenuOpen, setIsSourceMenuOpen] = useState(false)
  // The menu is anchored to the whole pill so it spans the CTA's full width
  const pillRef = useRef<HTMLDivElement>(null)

  const closeSourceMenu = () => setIsSourceMenuOpen(false)

  const handleSourceSelect = (option: GenerateSourceOption) => {
    closeSourceMenu()
    option.onSelect()
  }

  const helpTooltipContent = (
    <Stack gap={0.5} sx={{ padding: '0.8rem' }}>
      <Typography variant="text-sm" fontWeight={600}>
        {t('generate_button_tooltip.title')}
      </Typography>
      <Typography variant="text-sm">
        {t('generate_button_tooltip.description')}
      </Typography>
    </Stack>
  )

  return (
    <Box
      sx={{
        borderTop: `1px solid ${GRAY_300}`,
        paddingTop: '0.8rem',
        paddingBottom: '0.8rem',
        marginTop: 'auto',
        marginLeft: `-${padding}`,
        marginRight: `-${padding}`,
        paddingLeft: padding,
        paddingRight: padding,
        flexShrink: 0,
        flexGrow: 0,
        backgroundColor: WHITE,
      }}
    >
      <MUITooltip
        content={disabled ? (disabledTooltip || '') : ''}
        placement="top"
        arrow
      >
        <Stack ref={pillRef} flexDirection="row" sx={{ width: '100%', cursor: disabled ? 'not-allowed' : 'pointer' }}>

          <Button
            onClick={() => onGenerate()}
            disabled={disabled}
            sx={{
              ...CTA_BUTTON_SX,
              flex: 1,
              minWidth: 0,
              borderRadius: hasSourceMenu ? '40px 0 0 40px' : '40px',
              padding: '0.8rem 1.6rem',
              gap: '0.8rem',
              cursor: disabled ? 'not-allowed' : 'pointer',
            }}
          >

            {icon ?? <AutoAwesomeOutlined sx={{ fontSize: '2rem', color: 'inherit' }} />}

            <Typography
              sx={{
                fontSize: '1.4rem',
                fontWeight: 400,
                lineHeight: 1.4,
                letterSpacing: '-0.56px',
                color: 'inherit',
              }}
            >
              {label ?? t('generate_button')}
            </Typography>

            {helpPlacement === 'inline' && (
              <MUITooltip
                id="generate-btn-tooltip"
                enterDelay={500}
                placement="top"
                content={helpTooltipContent}
              >
                <HelpOutlineOutlined
                  sx={{
                    fontSize: '1.6rem',
                    color: 'inherit',
                    cursor: 'help',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                  onClick={(e) => e.stopPropagation()}
                />
              </MUITooltip>
            )}

          </Button>

          {hasSourceMenu && (
            <Button
              onClick={() => setIsSourceMenuOpen(open => !open)}
              disabled={disabled}
              aria-label={t('lab.source.menu_label')}
              aria-haspopup="menu"
              aria-expanded={isSourceMenuOpen}
              sx={{
                ...CTA_BUTTON_SX,
                minWidth: 0,
                width: '4.4rem',
                padding: 0,
                borderRadius: '0 40px 40px 0',
                borderLeft: '1px solid rgba(255, 255, 255, 0.25)',
                cursor: disabled ? 'not-allowed' : 'pointer',
              }}
            >
              <KeyboardArrowUpRounded
                sx={{
                  fontSize: '2rem',
                  color: 'inherit',
                  transition: 'transform .2s ease',
                  transform: isSourceMenuOpen ? 'rotate(180deg)' : 'none',
                }}
              />
            </Button>
          )}

        </Stack>
      </MUITooltip>

      {caption && (
        <Typography sx={{ marginTop: '0.6rem', textAlign: 'center', fontSize: '1.2rem', fontWeight: 350, lineHeight: 1.3, letterSpacing: '-0.24px', color: GRAY_700 }}>
          {caption}
        </Typography>
      )}

      {hasSourceMenu && (
        <Menu
          open={isSourceMenuOpen}
          anchorEl={pillRef.current}
          onClose={closeSourceMenu}
          anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
          transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          MenuListProps={{ dense: true, sx: { padding: 0 } }}
          slotProps={{
            paper: {
              sx: {
                width: pillRef.current?.offsetWidth,
                marginTop: '-0.8rem',
                padding: '0.4rem',
                borderRadius: '1.2rem',
                border: `1px solid ${GRAY_300}`,
                boxShadow: BOX_SHADOW_LG,
                backgroundColor: WHITE,
              },
            },
          }}
        >
          {sourceOptions.map((option) => (
            <MenuItem
              key={option.id}
              onClick={() => handleSourceSelect(option)}
              sx={{
                gap: '1rem',
                padding: '0.8rem',
                borderRadius: '0.8rem',
                alignItems: 'center',
                whiteSpace: 'normal',
                '&:hover': { backgroundColor: BEIGE_300 },
              }}
            >

              <Box
                component="img"
                src={option.imageUrl}
                alt=""
                sx={{
                  width: '3.6rem',
                  height: '3.6rem',
                  flexShrink: 0,
                  borderRadius: '0.6rem',
                  objectFit: 'cover',
                  border: `1px solid ${GRAY_300}`,
                  backgroundColor: GRAY_300,
                }}
              />

              <Stack sx={{ minWidth: 0 }}>

                <Typography sx={{ fontSize: '1.4rem', fontWeight: 500, lineHeight: 1.4, letterSpacing: '-0.56px', color: GRAY_1000 }}>
                  {option.title}
                </Typography>

                <Typography sx={{ fontSize: '1.2rem', fontWeight: 400, lineHeight: 1.4, letterSpacing: '-0.24px', color: GRAY_700 }}>
                  {option.description}
                </Typography>

              </Stack>

            </MenuItem>
          ))}
        </Menu>
      )}

    </Box>
  )
}
